import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import { apiService, getApiErrorMessage } from '../services/apiService';

interface MunkaszamData {
  datum: Date | null;
  projektKezdete: Date | null;
  tervezettBefejezes: Date | null;
  munkaTargya: string;
  megrendeloId: number | null;
  ugyintezoId: string;
  resztvevok: string[];
}

interface Megrendelo {
  id: number;
  cegnev: string;
}

interface User {
  id: string;
  userName: string;
  displayName: string;
}

interface AddMunkaszamDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddMunkaszamDialog: React.FC<AddMunkaszamDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<MunkaszamData>({
    datum: new Date(),
    projektKezdete: null,
    tervezettBefejezes: null,
    munkaTargya: '',
    megrendeloId: null,
    ugyintezoId: '',
    resztvevok: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Autocomplete states
  const [megrendeloOptions, setMegrendeloOptions] = useState<Megrendelo[]>([]);
  const [megrendeloLoading, setMegrendeloLoading] = useState(false);
  const [megrendeloInputValue, setMegrendeloInputValue] = useState('');
  
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userInputValue, setUserInputValue] = useState('');
  
  const [resztvevoOptions, setResztvevoOptions] = useState<User[]>([]);
  const [resztvevoLoading, setResztvevoLoading] = useState(false);
  const [resztvevoInputValue, setResztvevoInputValue] = useState('');

  // Megrendelő keresés
  const searchMegrendelok = async (query: string) => {
    if (!query || query.length < 2) {
      setMegrendeloOptions([]);
      return;
    }

    setMegrendeloLoading(true);
    try {
      const response = await apiService.get(`/Megrendelok/search?query=${encodeURIComponent(query)}`);
      setMegrendeloOptions(response.data || []);
    } catch (error) {
      console.error('Error searching megrendelok:', error);
      setMegrendeloOptions([]);
      setError(getApiErrorMessage(error, 'Nem sikerült a megrendelők keresése.'));
    } finally {
      setMegrendeloLoading(false);
    }
  };

  // User keresés (ügyintéző)
  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setUserOptions([]);
      return;
    }

    setUserLoading(true);
    try {
      const response = await apiService.get(`/Users/search?query=${encodeURIComponent(query)}`);
      setUserOptions(response.data || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setUserOptions([]);
      setError(getApiErrorMessage(error, 'Nem sikerült az ügyintézők keresése.'));
    } finally {
      setUserLoading(false);
    }
  };

  // Résztvevő keresés
  const searchResztvevok = async (query: string) => {
    if (!query || query.length < 2) {
      setResztvevoOptions([]);
      return;
    }

    setResztvevoLoading(true);
    try {
      const response = await apiService.get(`/Users/search?query=${encodeURIComponent(query)}`);
      setResztvevoOptions(response.data || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setResztvevoOptions([]);
      setError(getApiErrorMessage(error, 'Nem sikerült a résztvevők keresése.'));
    } finally {
      setResztvevoLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.datum) {
      setError('Dátum megadása kötelező!');
      return;
    }

    if (!formData.munkaTargya.trim()) {
      setError('Munka tárgya megadása kötelező!');
      return;
    }

    if (!formData.megrendeloId) {
      setError('Megrendelő kiválasztása kötelező!');
      return;
    }

    if (!formData.ugyintezoId) {
      setError('Ügyintéző kiválasztása kötelező!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const toLocalDateTimeString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T12:00:00`;
      };

      // Create the data object for the API
      const munkaszamData = {
        id: 0, // Új rekord esetén 0
        ev: formData.datum!.getFullYear(),
        sorszam: 0, // Ez automatikusan generálódik a backend-en
        datum: formData.datum!,
        projektKezdete: formData.projektKezdete
          ? toLocalDateTimeString(formData.projektKezdete)
          : null,
        tervezettBefejezes: formData.tervezettBefejezes
          ? toLocalDateTimeString(formData.tervezettBefejezes)
          : null,
        megrendelo: '', // Ez a backend tölti ki
        torzsszam: formData.megrendeloId!.toString(),
        munkaszam: '', // Ez a backend tölti ki
        munkaTargya: formData.munkaTargya.trim(),
        ugyintezo: '', // Ez a backend tölti ki
        resztvevok: formData.resztvevok.join(';'), // Pontosvesszővel elválasztva
        megrendeloId: formData.megrendeloId!,
        ugyintezoId: formData.ugyintezoId,
      };

      await apiService.createData('munkaszamok', munkaszamData);
      
      // Reset form
      resetForm();
      
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating munkaszam:', err);
      setError(getApiErrorMessage(err, 'Hiba történt a munkaszám létrehozása során.'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      datum: new Date(),
      projektKezdete: null,
      tervezettBefejezes: null,
      munkaTargya: '',
      megrendeloId: null,
      ugyintezoId: '',
      resztvevok: [],
    });
    setError(null);
    setMegrendeloOptions([]);
    setUserOptions([]);
    setResztvevoOptions([]);
    setMegrendeloInputValue('');
    setUserInputValue('');
    setResztvevoInputValue('');
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Dialog 
        open={open} 
        onClose={handleCancel} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
        <DialogTitle>Új Munkaszám Hozzáadása</DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Dátum */}
              <DatePicker
                label="Dátum *"
                value={formData.datum}
                onChange={(newValue) => {
                  setFormData(prev => ({ ...prev, datum: newValue }));
                  if (error) setError(null);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error && !formData.datum,
                    helperText: !!error && !formData.datum ? 'Dátum megadása kötelező' : '',
                  }
                }}
              />

              {/* Projekt kezdete */}
              <DatePicker
                label="Projekt kezdete"
                value={formData.projektKezdete}
                onChange={(newValue) => {
                  setFormData(prev => ({ ...prev, projektKezdete: newValue }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />

              {/* Tervezett befejezés */}
              <DatePicker
                label="Tervezett befejezés"
                value={formData.tervezettBefejezes}
                onChange={(newValue) => {
                  setFormData(prev => ({ ...prev, tervezettBefejezes: newValue }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />

              {/* Megrendelő */}
              <Autocomplete
                options={megrendeloOptions}
                getOptionLabel={(option) => option.cegnev}
                loading={megrendeloLoading}
                inputValue={megrendeloInputValue}
                onInputChange={(event, newInputValue) => {
                  setMegrendeloInputValue(newInputValue);
                  searchMegrendelok(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, megrendeloId: newValue?.id || null }));
                  if (error) setError(null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Megrendelő *"
                    error={!!error && !formData.megrendeloId}
                    helperText={!!error && !formData.megrendeloId ? 'Megrendelő kiválasztása kötelező' : ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {megrendeloLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Munka tárgya */}
              <TextField
                label="Munka tárgya *"
                fullWidth
                multiline
                rows={3}
                value={formData.munkaTargya}
                onChange={(event) => {
                  setFormData(prev => ({ ...prev, munkaTargya: event.target.value }));
                  if (error) setError(null);
                }}
                error={!!error && !formData.munkaTargya.trim()}
                helperText={!!error && !formData.munkaTargya.trim() ? 'Munka tárgya megadása kötelező' : ''}
              />

              {/* Ügyintéző */}
              <Autocomplete
                options={userOptions}
                getOptionLabel={(option) => option.displayName}
                loading={userLoading}
                inputValue={userInputValue}
                onInputChange={(event, newInputValue) => {
                  setUserInputValue(newInputValue);
                  searchUsers(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, ugyintezoId: newValue?.id || '' }));
                  if (error) setError(null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ügyintéző *"
                    error={!!error && !formData.ugyintezoId}
                    helperText={!!error && !formData.ugyintezoId ? 'Ügyintéző kiválasztása kötelező' : ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Résztvevők */}
              <Autocomplete
                multiple
                options={resztvevoOptions}
                getOptionLabel={(option) => option.displayName}
                loading={resztvevoLoading}
                inputValue={resztvevoInputValue}
                onInputChange={(event, newInputValue) => {
                  setResztvevoInputValue(newInputValue);
                  searchResztvevok(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    resztvevok: newValue.map(user => user.displayName) 
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Résztvevők"
                    helperText="Több résztvevő is kiválasztható"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {resztvevoLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCancel} 
            disabled={loading}
            variant="outlined"
          >
            Mégse
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Létrehozás...' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddMunkaszamDialog;
